import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import Api from '../../../../../common/Api';
import {StringNullable} from '../../../../../common/Types';
import {ButtonLayout, NegativeButtons, PositiveButtons} from '../../../../../components/button/ButtonLayout';
import {CancelButton} from '../../../../../components/button/CancelButton';
import {SaveButton} from '../../../../../components/button/SaveButton';
import Dialog from '../../../../../components/dialogs/Dialog';
import DialogActions from '../../../../../components/dialogs/DialogActions';
import DialogContent from '../../../../../components/dialogs/DialogContent';
import DialogTitle from '../../../../../components/dialogs/DialogTitle';
import Grid from '../../../../../components/dialogs/Grid';
import SingleSelectField from '../../../../../components/fields/select/SingleSelectField';
import TextField from '../../../../../components/fields/textfield/TextField';
import {_transl} from '../../../../../store/localization/TranslMessasge';
import {IApplicationState} from '../../../../../store/Store';
import {CommonTranslationKey} from '../../CommonTranslationKey';
import {ErrorTranslationKey} from '../../ErrorTranslationKey';
import Snackbar from '../../snackbar/Snackbar';
import {DiagramTranslationKey} from '../DiagramTranslationKey';

export interface DiagramDetailsDto {
    name: string;
    description: StringNullable;
    viewpoint: StringNullable;
}

interface DiagramUpdateDialogProps {
    open: boolean;
    diagramId: string;
    diagramDetails: DiagramDetailsDto;
    onUpdated: () => void;
    onClose: () => void;
}

export default function DiagramUpdateDialog({open, diagramId, diagramDetails, onUpdated, onClose}: Readonly<DiagramUpdateDialogProps>) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [viewpoint, setViewpoint] = useState<string | null>(null);
    const [inProgress, setInProgress] = useState(false);

    const viewpoints = useSelector((state: IApplicationState) => state.pages.common.options.viewpoints.resource);
    const viewpointOptions = viewpoints.map((vp) => vp.identifier);
    const viewpointLabelMap = new Map(viewpoints.map((vp) => ([vp.identifier, vp.name])));

    useEffect(() => {
        if (open) {
            setName(diagramDetails.name ?? '');
            setDescription(diagramDetails.description ?? '');
            setViewpoint(diagramDetails.viewpoint ?? null);
        }
    }, [open, diagramDetails]);

    function updateDiagram() {
        setInProgress(true);

        Api.diagrams.updateDiagramDetails(diagramId, {
            name: name.trim(),
            description: description.trim(),
            viewpoint: viewpoint ?? '',
        })
                .then(() => {
                    onUpdated();
                    onClose();
                    Snackbar.success(_transl(CommonTranslationKey.DATA_SAVED_SUCCESSFULLY));
                })
                .catch((error) => Snackbar.error(_transl(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error))
                .finally(() => setInProgress(false));
    }

    return (
            <Dialog open={open} onClose={onClose} maxWidth="md">
                <DialogTitle title={_transl(DiagramTranslationKey.EDIT_DIAGRAM_DETAILS)}
                        onDialogClosed={onClose}/>
                <DialogContent>
                    <Grid container>
                        <Grid item xs={12} md={7}>
                            <TextField
                                    label={_transl(DiagramTranslationKey.DETAIL_NAME)}
                                    value={name}
                                    onChange={value => setName(value)}/>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <SingleSelectField
                                    id="diagram-update-viewpoint"
                                    label={_transl(DiagramTranslationKey.DETAIL_VIEWPOINT)}
                                    value={viewpoint}
                                    onChange={value => setViewpoint(value)}
                                    options={viewpointOptions}
                                    getOptionLabel={(option) => viewpointLabelMap.get(option) ?? option}
                                    acceptFreeText={true}
                                    required={false}/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                    label={_transl(DiagramTranslationKey.DETAIL_DESCRIPTION)}
                                    value={description}
                                    onChange={value => setDescription(value)}
                                    multiline
                                    rows={5}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <ButtonLayout>
                        <PositiveButtons>
                            <SaveButton onClick={updateDiagram} disabled={inProgress}/>
                        </PositiveButtons>
                        <NegativeButtons>
                            <CancelButton onClick={onClose} disabled={inProgress}/>
                        </NegativeButtons>
                    </ButtonLayout>
                </DialogActions>
            </Dialog>
    );
}
